// Billboard sponsor menyala saat malam: panel iklan berpendar + dua lampu sorot di bingkai atas (dilukis di atas selubung malam).
import { TILE_W } from "./iso";
import { refHeight } from "./paint";
import { gambarSponsor } from "./sponsorGambar";

// Panel {u0,u1,vF,z0,z1} (z × T) → 4 titik layar, memakai sudut tapak (dengan rotasi) + offset atap (dz) untuk billboard di atas gedung.
function sudutPanel(b, def, gridToScreen, zoom, dz) {
  let w = def.w || 1, h = def.h || 1;
  const rot = b.rot || 0;
  if (rot % 2 === 1) [w, h] = [h, w];
  const c = [gridToScreen(b.x, b.y), gridToScreen(b.x + w, b.y), gridToScreen(b.x + w, b.y + h), gridToScreen(b.x, b.y + h)];
  const [top, right, , left] = [0, 1, 2, 3].map(i => c[(i + rot) % 4]);
  const T = TILE_W * zoom, p = def.panel;
  const pt = (u, v, z) => [top[0] + u * (right[0] - top[0]) + v * (left[0] - top[0]), top[1] + u * (right[1] - top[1]) + v * (left[1] - top[1]) - z - dz];
  return { a: pt(p.u0, p.vF, p.z1 * T), b: pt(p.u1, p.vF, p.z1 * T), c: pt(p.u1, p.vF, p.z0 * T), d: pt(p.u0, p.vF, p.z0 * T), T };
}

export function drawBillboardGlow(ctx, state, defs, gridToScreen, cam, atmos, tileMap, W, H) {
  const ni = Math.max(0, (atmos.dark - 0.15) / 0.85);   // 0 siang → 1 malam
  const byId = {};
  for (const d of defs || []) if (d.panel) byId[d.bid] = d;
  ctx.save();
  for (const b of state.buildings || []) {
    const def = byId[b.bid];
    if (!def) continue;
    let dz = 0;
    if (def.atap) {
      const bawah = tileMap[b.x + "," + b.y];
      if (bawah && !bawah.def?.atap) {
        let bw = bawah.def?.w || 1, bh = bawah.def?.h || 1;
        if ((bawah.b.rot || 0) % 2 === 1) [bw, bh] = [bh, bw];
        dz = refHeight(bawah.def?.floors ?? 1, bw, bh, cam.zoom);
      }
    }
    const q = sudutPanel(b, def, gridToScreen, cam.zoom, dz);
    const xs = [q.a[0], q.b[0], q.c[0], q.d[0]], ys = [q.a[1], q.b[1], q.c[1], q.d[1]];
    if (Math.max(...xs) < -60 || Math.min(...xs) > W + 60 || Math.max(...ys) < -60 || Math.min(...ys) > H + 60) continue;
    const T = q.T;
    // arah "ke depan" (menjauhi bidang panel, ke arah penonton) = sumbu v iso di layar, dinormalkan
    let fx = q.d[0] - q.a[0], fy = q.d[1] - q.a[1];            // sementara: vektor bawah
    const ux = (q.b[0] - q.a[0]), uy = (q.b[1] - q.a[1]);       // vektor lebar panel (sepanjang u)
    fx = -uy; fy = ux; const fl = Math.hypot(fx, fy) || 1; fx /= fl; fy /= fl; if (fy < 0) { fx = -fx; fy = -fy; }
    const maju = 0.10 * T;                                      // panjang lengan besi ke depan
    const bawahKiri = q.d, bawahKanan = q.c;
    const pL = [bawahKiri[0] + fx * maju, bawahKiri[1] + fy * maju], pR = [bawahKanan[0] + fx * maju, bawahKanan[1] + fy * maju];

    if (ni > 0) {
      // Panel "menyala": gambar iklan dilukis ulang di atas selubung malam (tajam, tidak redup), rona hanya dari lampu depan-bawah
      const img = gambarSponsor(b.sponsor?.gambar_id);
      ctx.globalAlpha = Math.min(1, ni * 1.2);
      if (img) {
        const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
        ctx.save();
        ctx.transform((q.b[0] - q.a[0]) / w, (q.b[1] - q.a[1]) / w, (q.d[0] - q.a[0]) / h, (q.d[1] - q.a[1]) / h, q.a[0], q.a[1]);
        ctx.drawImage(img, 0, 0, w, h);
        ctx.restore();
      } else {
        ctx.fillStyle = "rgba(250,247,238,0.92)";
        ctx.beginPath(); ctx.moveTo(...q.a); ctx.lineTo(...q.b); ctx.lineTo(...q.c); ctx.lineTo(...q.d); ctx.closePath(); ctx.fill();
      }
      // sapuan cahaya dari bawah-depan: lebih terang di tepi bawah panel, memudar ke atas (lighter, lembut)
      ctx.globalCompositeOperation = "lighter";
      const mid = (p1, p2) => [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
      const g = ctx.createLinearGradient(...mid(q.d, q.c), ...mid(q.a, q.b));
      g.addColorStop(0, `rgba(255,238,200,${(0.22 * ni).toFixed(3)})`); g.addColorStop(0.6, "rgba(255,238,200,0)");
      ctx.fillStyle = g; ctx.beginPath(); ctx.moveTo(...q.a); ctx.lineTo(...q.b); ctx.lineTo(...q.c); ctx.lineTo(...q.d); ctx.closePath(); ctx.fill();
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 1;
    }

    // Lengan besi lampu di depan-bawah panel (selalu tampak, siang & malam): dua penyangga + palang + 2 kepala lampu menghadap ke atas
    ctx.lineCap = "round";
    ctx.strokeStyle = "rgb(62,66,72)"; ctx.lineWidth = Math.max(1, 0.012 * T);
    ctx.beginPath(); ctx.moveTo(...bawahKiri); ctx.lineTo(...pL); ctx.moveTo(...bawahKanan); ctx.lineTo(...pR); ctx.moveTo(...pL); ctx.lineTo(...pR); ctx.stroke();
    for (const t of [0.22, 0.78]) {
      const hx = pL[0] + (pR[0] - pL[0]) * t, hy = pL[1] + (pR[1] - pL[1]) * t;
      const r = 0.022 * T;
      ctx.fillStyle = "rgb(62,66,72)";
      ctx.beginPath(); ctx.ellipse(hx, hy, r * 1.3, r * 0.8, 0, 0, 7); ctx.fill();          // rumah lampu
      if (ni > 0) {                                                                           // muka lampu menyala (menghadap panel di atasnya)
        ctx.fillStyle = `rgba(255,246,220,${(0.9 * ni).toFixed(3)})`;
        ctx.beginPath(); ctx.ellipse(hx, hy - r * 0.5, r * 0.9, r * 0.45, 0, 0, 7); ctx.fill();
      }
    }
  }
  ctx.restore();
}
